<template>
  <div class="page-card">
    <h2 class="page-title">订单管理</h2>

    <div class="toolbar">
      <div class="toolbar-left">
        <el-button @click="goPrivateMessages()">私信管理</el-button>
        <el-input
          v-model="query.keyword"
          placeholder="订单号/通告编号/通告标题/商家/艺人/报名编号/邀约编号"
          clearable
          style="width: 360px"
          @keyup.enter.native="fetchData"
        />
        <el-select v-model="query.sourceType" clearable placeholder="来源类型" style="width: 140px">
          <el-option label="报名录用" :value="1" />
          <el-option label="邀约合作" :value="2" />
        </el-select>
        <el-select v-model="query.fulfillmentStatus" clearable placeholder="履约状态" style="width: 140px">
          <el-option label="进行中" :value="1" />
          <el-option label="待验收" :value="2" />
          <el-option label="已验收" :value="3" />
          <el-option label="已失效" :value="4" />
        </el-select>
        <el-select v-model="query.settlementStatus" clearable placeholder="结算状态" style="width: 140px">
          <el-option label="待结算" :value="1" />
          <el-option label="已结算" :value="2" />
        </el-select>
        <el-button type="primary" @click="fetchData">查询</el-button>
      </div>
    </div>

    <el-table :data="list" border>
      <el-table-column prop="orderNo" label="订单编号" min-width="180" />
      <el-table-column label="通告信息" min-width="240">
        <template slot-scope="{ row }">
          <div class="cell-block">
            <div class="cell-title">{{ row.noticeTitle || '-' }}</div>
            <div class="cell-sub">通告编号：{{ row.noticeNo || '-' }}</div>
          </div>
        </template>
      </el-table-column>
      <el-table-column label="来源信息" min-width="180">
        <template slot-scope="{ row }">
          <div class="cell-block">
            <div class="cell-title">{{ row.sourceText || '-' }}</div>
            <div class="cell-sub">来源编号：{{ row.sourceBizNo || '-' }}</div>
          </div>
        </template>
      </el-table-column>
      <el-table-column label="商家信息" min-width="180">
        <template slot-scope="{ row }">
          <div class="cell-block">
            <div class="cell-title">{{ row.merchantName || '-' }}</div>
            <el-button
              v-if="row.merchantUserId"
              type="text"
              class="inline-link"
              @click="openMerchantDetailById(row.merchantUserId)"
            >
              查看商家详情
            </el-button>
          </div>
        </template>
      </el-table-column>
      <el-table-column label="艺人信息" min-width="180">
        <template slot-scope="{ row }">
          <div class="cell-block">
            <div class="cell-title">{{ row.artistName || '-' }}</div>
            <el-button
              v-if="row.artistUserId"
              type="text"
              class="inline-link"
              @click="openArtistDetailById(row.artistUserId)"
            >
              查看艺人详情
            </el-button>
          </div>
        </template>
      </el-table-column>
      <el-table-column prop="fulfillmentStatusText" label="履约状态" width="120" />
      <el-table-column prop="settlementStatusText" label="结算状态" width="120" />
      <el-table-column label="异常关闭" width="100">
        <template slot-scope="{ row }">
          <el-tag :type="row.closed ? 'danger' : 'success'">{{ row.closed ? '是' : '否' }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column label="活动时间" min-width="200">
        <template slot-scope="{ row }">
          <div>{{ formatRange(row.activityStartAt, row.activityEndAt) }}</div>
        </template>
      </el-table-column>
      <el-table-column prop="createdAt" label="创建时间" width="180" />
      <el-table-column label="操作" width="180" fixed="right">
        <template slot-scope="{ row }">
          <el-button type="text" @click="openDetail(row)">查看详情</el-button>
          <el-button v-if="row.merchantUserId && row.artistUserId" type="text" @click="goPrivateMessages(row.merchantUserId, row.artistUserId)">查看私信</el-button>
        </template>
      </el-table-column>
    </el-table>

    <el-dialog title="订单详情" :visible.sync="detailVisible" width="1080px">
      <div v-if="detail.id" class="order-detail-wrap">
        <div class="detail-action-bar">
          <el-button
            v-if="detail.merchantUserId && detail.artistUserId"
            size="small"
            @click="goPrivateMessages(detail.merchantUserId, detail.artistUserId)"
          >查看私信</el-button>
          <el-button v-if="canCloseCurrentOrder" type="danger" size="small" @click="openCloseDialog">关闭订单</el-button>
          <el-button v-if="canDeleteCurrentOrder" type="danger" plain size="small" @click="handleDeleteOrder">删除订单</el-button>
        </div>

        <el-descriptions title="订单基础信息" :column="2" border class="detail-section">
          <el-descriptions-item label="订单编号">{{ detail.orderNo || '-' }}</el-descriptions-item>
          <el-descriptions-item label="创建时间">{{ detail.createdAt || '-' }}</el-descriptions-item>
          <el-descriptions-item label="来源类型">{{ detail.sourceText || '-' }}</el-descriptions-item>
          <el-descriptions-item label="来源编号">{{ detail.sourceBizNo || '-' }}</el-descriptions-item>
          <el-descriptions-item label="履约状态">{{ detail.fulfillmentStatusText || '-' }}</el-descriptions-item>
          <el-descriptions-item label="结算状态">{{ detail.settlementStatusText || '-' }}</el-descriptions-item>
        </el-descriptions>

        <el-descriptions title="通告信息" :column="2" border class="detail-section">
          <el-descriptions-item label="通告ID">{{ detail.noticeId || '-' }}</el-descriptions-item>
          <el-descriptions-item label="通告编号">{{ detail.noticeNo || '-' }}</el-descriptions-item>
          <el-descriptions-item label="通告标题" :span="2">{{ detail.noticeTitle || '-' }}</el-descriptions-item>
          <el-descriptions-item label="活动开始">{{ detail.activityStartAt || '-' }}</el-descriptions-item>
          <el-descriptions-item label="活动结束">{{ detail.activityEndAt || '-' }}</el-descriptions-item>
          <el-descriptions-item label="工作城市">{{ detail.workCity || '-' }}</el-descriptions-item>
          <el-descriptions-item label="工作区域">{{ detail.workArea || '-' }}</el-descriptions-item>
          <el-descriptions-item label="详细地点" :span="2">{{ detail.locationDetail || '-' }}</el-descriptions-item>
          <el-descriptions-item label="费用说明" :span="2">{{ detail.salaryDesc || '-' }}</el-descriptions-item>
        </el-descriptions>

        <el-descriptions title="关联业务信息" :column="2" border class="detail-section">
          <el-descriptions-item label="主来源类型">{{ detail.sourceText || '-' }}</el-descriptions-item>
          <el-descriptions-item label="主来源编号">{{ detail.sourceBizNo || '-' }}</el-descriptions-item>
          <el-descriptions-item label="报名ID">{{ detail.applicationId || '-' }}</el-descriptions-item>
          <el-descriptions-item label="报名编号">
            <span>{{ detail.applicationNo || '-' }}</span>
            <el-tag v-if="detail.sourceType === 1" size="mini" type="success" class="source-tag">主来源</el-tag>
          </el-descriptions-item>
          <el-descriptions-item label="邀约ID">{{ detail.invitationId || '-' }}</el-descriptions-item>
          <el-descriptions-item label="邀约编号">
            <span>{{ detail.invitationNo || '-' }}</span>
            <el-tag v-if="detail.sourceType === 2" size="mini" type="warning" class="source-tag">主来源</el-tag>
          </el-descriptions-item>
        </el-descriptions>

        <el-descriptions title="关联主体信息" :column="2" border class="detail-section">
          <el-descriptions-item label="商家ID">
            <span>{{ detail.merchantUserId || '-' }}</span>
            <el-button
              v-if="detail.merchantUserId"
              type="text"
              class="inline-link"
              @click="openMerchantDetailById(detail.merchantUserId)"
            >
              查看商家详情
            </el-button>
          </el-descriptions-item>
          <el-descriptions-item label="商家名称">{{ detail.merchantName || '-' }}</el-descriptions-item>
          <el-descriptions-item label="艺人用户ID">
            <span>{{ detail.artistUserId || '-' }}</span>
            <el-button
              v-if="detail.artistUserId"
              type="text"
              class="inline-link"
              @click="openArtistDetailById(detail.artistUserId)"
            >
              查看艺人详情
            </el-button>
          </el-descriptions-item>
          <el-descriptions-item label="艺人名称">{{ detail.artistName || '-' }}</el-descriptions-item>
          <el-descriptions-item label="艺人档案ID">{{ detail.artistProfileId || detail.artistId || '-' }}</el-descriptions-item>
          <el-descriptions-item label="订单ID">{{ detail.id || '-' }}</el-descriptions-item>
        </el-descriptions>

        <el-descriptions title="关闭信息" :column="2" border class="detail-section">
          <el-descriptions-item label="是否关闭">{{ detail.closed ? '是' : '否' }}</el-descriptions-item>
          <el-descriptions-item label="关闭时间">{{ detail.closedAt || '-' }}</el-descriptions-item>
          <el-descriptions-item label="关闭原因" :span="2">{{ detail.closeReason || '-' }}</el-descriptions-item>
        </el-descriptions>

        <div class="detail-section">
          <div class="section-title">订单照片与资料</div>
          <div v-if="detailMediaList.length">
            <div class="media-tag-list">
              <el-tag
                v-for="item in detailMediaList"
                :key="item.id || item.mediaUrl"
                :type="mediaTagType(item.mediaTypeText)"
                size="small"
              >
                {{ item.mediaTypeText || '未分类' }}
              </el-tag>
            </div>

            <el-divider>图片资料</el-divider>
            <div v-if="detailImageMediaList.length" class="media-grid">
              <div v-for="item in detailImageMediaList" :key="item.id || item.mediaUrl" class="detail-media-card">
                <el-image
                  :src="item.mediaUrl"
                  :preview-src-list="detailPreviewImages"
                  fit="cover"
                  class="media-image"
                />
                <div class="detail-media-meta">
                  <el-tag size="mini" :type="mediaTagType(item.mediaTypeText)">{{ item.mediaTypeText || '未分类' }}</el-tag>
                  <span>{{ item.remark || '无备注' }}</span>
                </div>
              </div>
            </div>
            <el-empty v-else description="暂无图片资料" :image-size="88" />

            <el-divider>视频资料</el-divider>
            <div v-if="detailVideoMediaList.length" class="media-grid video-grid">
              <div v-for="item in detailVideoMediaList" :key="item.id || item.mediaUrl" class="video-card">
                <video
                  class="media-video"
                  :src="item.mediaUrl"
                  :poster="item.coverUrl || ''"
                  controls
                  preload="metadata"
                />
                <div class="detail-media-meta">
                  <el-tag size="mini" :type="mediaTagType(item.mediaTypeText)">{{ item.mediaTypeText || '未分类' }}</el-tag>
                  <span>{{ item.remark || '无备注' }}</span>
                </div>
                <div class="video-actions">
                  <el-button size="mini" type="primary" plain @click="openVideoPreview(item)">放大预览</el-button>
                </div>
              </div>
            </div>
            <el-empty v-else description="暂无视频资料" :image-size="88" />
          </div>
          <el-empty v-else description="暂无订单资料" :image-size="88" />
        </div>

        <div class="detail-section">
          <div class="section-title">双方互评</div>
          <div class="review-grid">
            <div class="review-card">
              <div class="review-title">商家评价</div>
              <div v-if="detail.merchantReview">
                <div class="review-meta">
                  <span>{{ detail.merchantReview.operatorName || '-' }}</span>
                  <span>{{ detail.merchantReview.createdAt || '-' }}</span>
                </div>
                <div class="review-content">{{ detail.merchantReview.content || '-' }}</div>
              </div>
              <el-empty v-else description="商家暂未评价" :image-size="68" />
            </div>
            <div class="review-card">
              <div class="review-title">艺人评价</div>
              <div v-if="detail.artistReview">
                <div class="review-meta">
                  <span>{{ detail.artistReview.operatorName || '-' }}</span>
                  <span>{{ detail.artistReview.createdAt || '-' }}</span>
                </div>
                <div class="review-content">{{ detail.artistReview.content || '-' }}</div>
              </div>
              <el-empty v-else description="艺人暂未评价" :image-size="68" />
            </div>
          </div>
        </div>

        <div class="detail-section">
          <div class="section-title">操作日志</div>
          <el-timeline v-if="detail.logs && detail.logs.length">
            <el-timeline-item v-for="item in detail.logs" :key="item.id" :timestamp="item.createdAt">
              <div class="log-item-title">{{ item.operatorName || '系统' }}</div>
              <div class="log-item-desc">{{ item.remark || '订单状态更新' }}</div>
            </el-timeline-item>
          </el-timeline>
          <el-empty v-else description="暂无日志" :image-size="88" />
        </div>
      </div>
    </el-dialog>

    <el-dialog title="关闭订单" :visible.sync="closeDialogVisible" width="520px">
      <el-form label-width="90px">
        <el-form-item label="订单ID">
          <el-input :value="closeForm.orderId" disabled />
        </el-form-item>
        <el-form-item label="关闭原因">
          <el-input
            v-model="closeForm.closeReason"
            type="textarea"
            :rows="4"
            maxlength="200"
            show-word-limit
            placeholder="请输入关闭订单原因"
          />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="closeDialogVisible = false">取消</el-button>
        <el-button type="danger" @click="submitCloseOrder">确认关闭</el-button>
      </span>
    </el-dialog>

    <el-dialog title="艺人详情" :visible.sync="artistDetailVisible" width="920px">
      <div v-if="artistDetail.userId">
        <el-descriptions :column="2" border>
          <el-descriptions-item label="艺名">{{ artistDetail.artistName || '-' }}</el-descriptions-item>
          <el-descriptions-item label="昵称">{{ artistDetail.nickname || '-' }}</el-descriptions-item>
          <el-descriptions-item label="手机号">{{ maskMobile(artistDetail.mobile) }}</el-descriptions-item>
          <el-descriptions-item label="状态">{{ artistDetail.userStatus === 1 ? '正常' : '封禁' }}</el-descriptions-item>
          <el-descriptions-item label="城市">{{ artistDetail.city || '-' }}</el-descriptions-item>
          <el-descriptions-item label="年龄">{{ artistDetail.age || '-' }}</el-descriptions-item>
          <el-descriptions-item label="身高">{{ artistDetail.heightCm || '-' }}</el-descriptions-item>
          <el-descriptions-item label="体重">{{ artistDetail.weightKg || '-' }}</el-descriptions-item>
          <el-descriptions-item label="技能标签" :span="2">{{ artistDetail.skillTags || '-' }}</el-descriptions-item>
          <el-descriptions-item label="通告偏好" :span="2">{{ artistDetail.noticeTypes || '-' }}</el-descriptions-item>
          <el-descriptions-item label="经验描述" :span="2">{{ artistDetail.experienceDesc || '-' }}</el-descriptions-item>
          <el-descriptions-item label="档期说明" :span="2">{{ artistDetail.scheduleDesc || '-' }}</el-descriptions-item>
        </el-descriptions>

        <el-divider>照片资料</el-divider>
        <div v-if="artistImageMediaList.length" class="media-grid">
          <el-image
            v-for="item in artistImageMediaList"
            :key="item.id || item.mediaUrl"
            :src="item.mediaUrl"
            :preview-src-list="artistPreviewImages"
            fit="cover"
            class="media-image"
          />
        </div>
        <el-empty v-else description="暂无照片资料" :image-size="88" />

        <el-divider>视频资料</el-divider>
        <div v-if="artistVideoMediaList.length" class="media-grid video-grid">
          <div v-for="item in artistVideoMediaList" :key="item.id || item.mediaUrl" class="video-card">
            <video
              class="media-video"
              :src="item.mediaUrl"
              :poster="item.coverUrl || ''"
              controls
              preload="metadata"
            />
            <div class="video-actions">
              <el-button size="mini" type="primary" plain @click="openVideoPreview(item)">放大预览</el-button>
            </div>
          </div>
        </div>
        <el-empty v-else description="暂无视频资料" :image-size="88" />
      </div>
    </el-dialog>

    <el-dialog title="商家详情" :visible.sync="merchantDetailVisible" width="900px">
      <div v-if="merchantDetail.userId">
        <el-descriptions :column="2" border>
          <el-descriptions-item label="状态">{{ merchantDetail.userStatus === 1 ? '正常' : '封禁' }}</el-descriptions-item>
          <el-descriptions-item label="商家名称">{{ merchantDetail.merchantName || '-' }}</el-descriptions-item>
          <el-descriptions-item label="品牌名称">{{ merchantDetail.brandName || '-' }}</el-descriptions-item>
          <el-descriptions-item label="手机号">{{ maskMobile(merchantDetail.mobile) }}</el-descriptions-item>
          <el-descriptions-item label="联系人">{{ merchantDetail.contactName || '-' }}</el-descriptions-item>
          <el-descriptions-item label="联系人手机号">{{ maskMobile(merchantDetail.contactMobile) }}</el-descriptions-item>
          <el-descriptions-item label="微信">{{ merchantDetail.contactWechat || '-' }}</el-descriptions-item>
          <el-descriptions-item label="认证状态">{{ authStatusText(merchantDetail.authStatus) }}</el-descriptions-item>
          <el-descriptions-item label="商家类型">{{ merchantDetail.merchantType || '-' }}</el-descriptions-item>
          <el-descriptions-item label="简介" :span="2">{{ merchantDetail.intro || '-' }}</el-descriptions-item>
        </el-descriptions>

        <el-divider>实名信息</el-divider>
        <el-descriptions :column="2" border>
          <el-descriptions-item label="认证状态">{{ authStatusText(merchantDetail.authStatus) }}</el-descriptions-item>
          <el-descriptions-item label="认证类型">{{ authTypeText(merchantDetail.authType) }}</el-descriptions-item>
          <el-descriptions-item label="主体/公司名称">{{ merchantDetail.authCompanyName || '-' }}</el-descriptions-item>
          <el-descriptions-item label="法人姓名">{{ merchantDetail.authLegalPersonName || '-' }}</el-descriptions-item>
          <el-descriptions-item label="提交时间">{{ merchantDetail.authSubmittedAt || '-' }}</el-descriptions-item>
          <el-descriptions-item label="审核时间">{{ merchantDetail.authAuditedAt || '-' }}</el-descriptions-item>
          <el-descriptions-item label="驳回原因" :span="2">{{ merchantDetail.authRejectReason || '-' }}</el-descriptions-item>
        </el-descriptions>

        <div v-if="authMaterialList.length" class="auth-material-grid">
          <div v-for="item in authMaterialList" :key="item.url" class="auth-material-card">
            <div class="auth-material-label">{{ item.label }}</div>
            <el-image
              :src="item.url"
              :preview-src-list="authPreviewImages"
              fit="cover"
              class="auth-material-image"
            />
          </div>
        </div>
        <el-empty v-else description="暂无实名材料" :image-size="88" />

        <el-divider>照片资料</el-divider>
        <div v-if="merchantImageList.length" class="media-grid">
          <el-image
            v-for="item in merchantImageList"
            :key="item.id || item.mediaUrl"
            :src="item.mediaUrl"
            :preview-src-list="merchantPreviewImages"
            fit="cover"
            class="media-image"
          />
        </div>
        <el-empty v-else description="暂无照片资料" :image-size="88" />

        <el-divider>视频资料</el-divider>
        <div v-if="merchantVideoList.length" class="media-grid video-grid">
          <div v-for="item in merchantVideoList" :key="item.id || item.mediaUrl" class="video-card">
            <video
              class="media-video"
              :src="item.mediaUrl"
              :poster="item.coverUrl || ''"
              controls
              preload="metadata"
            />
            <div class="video-actions">
              <el-button size="mini" type="primary" plain @click="openVideoPreview(item)">放大预览</el-button>
            </div>
          </div>
        </div>
        <el-empty v-else description="暂无视频资料" :image-size="88" />
      </div>
    </el-dialog>

    <el-dialog
      title="视频预览"
      :visible.sync="videoPreviewVisible"
      width="960px"
      top="6vh"
      append-to-body
      @closed="handleVideoPreviewClosed"
    >
      <div class="video-preview-wrap">
        <video
          v-if="previewVideo.mediaUrl"
          ref="previewVideoPlayer"
          class="preview-video-player"
          :src="previewVideo.mediaUrl"
          :poster="previewVideo.coverUrl || ''"
          controls
          autoplay
          preload="auto"
        />
      </div>
      <span slot="footer">
        <el-button @click="videoPreviewVisible = false">关闭</el-button>
        <el-button type="primary" @click="requestVideoFullscreen">全屏播放</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { MessageBox } from 'element-ui'
import {
  closePlatformOrder,
  deletePlatformOrder,
  getArtistUserDetail,
  getMerchantUserDetail,
  getPlatformOrderDetail,
  getPlatformOrders
} from '../../../api/platformManage'
import { isImageMedia, isVideoMedia, maskMobile, normalizeMediaItem } from '../../../utils/format'

export default {
  name: 'PlatformOrdersPage',
  data() {
    return {
      list: [],
      detailVisible: false,
      detail: {},
      artistDetailVisible: false,
      artistDetail: {},
      merchantDetailVisible: false,
      merchantDetail: {},
      videoPreviewVisible: false,
      previewVideo: {},
      closeDialogVisible: false,
      closeForm: {
        orderId: '',
        closeReason: ''
      },
      query: {
        keyword: '',
        fulfillmentStatus: undefined,
        settlementStatus: undefined,
        sourceType: undefined
      }
    }
  },
  computed: {
    artistMediaList() {
      return (this.artistDetail.mediaList || []).map(normalizeMediaItem).filter(Boolean)
    },
    artistImageMediaList() {
      return this.artistMediaList.filter(item => isImageMedia(item))
    },
    artistVideoMediaList() {
      return this.artistMediaList.filter(item => isVideoMedia(item))
    },
    artistPreviewImages() {
      return this.artistImageMediaList.map(item => item.mediaUrl).filter(Boolean)
    },
    merchantMediaList() {
      if (Array.isArray(this.merchantDetail.mediaList) && this.merchantDetail.mediaList.length) {
        return this.merchantDetail.mediaList.map(normalizeMediaItem).filter(Boolean)
      }
      const list = []
      if (this.merchantDetail.logoUrl) {
        list.push(normalizeMediaItem({
          mediaType: 'image',
          mediaUrl: this.merchantDetail.logoUrl
        }))
      }
      return list.filter(Boolean)
    },
    merchantImageList() {
      return this.merchantMediaList.filter(item => isImageMedia(item))
    },
    merchantVideoList() {
      return this.merchantMediaList.filter(item => isVideoMedia(item))
    },
    merchantPreviewImages() {
      return this.merchantImageList.map(item => item.mediaUrl).filter(Boolean)
    },
    authMaterialList() {
      return [
        { label: '营业执照/主体资料', url: this.merchantDetail.authDocumentUrl },
        { label: '许可证资料', url: this.merchantDetail.authLicenseNoUrl },
        { label: '身份证人像面', url: this.merchantDetail.authCardFrontUrl },
        { label: '身份证国徽面', url: this.merchantDetail.authCardBackUrl }
      ].filter(item => item.url)
    },
    authPreviewImages() {
      return this.authMaterialList.map(item => item.url)
    },
    detailMediaList() {
      return (this.detail.mediaList || []).map(normalizeMediaItem).filter(Boolean)
    },
    detailImageMediaList() {
      return this.detailMediaList.filter(item => isImageMedia(item))
    },
    detailVideoMediaList() {
      return this.detailMediaList.filter(item => isVideoMedia(item))
    },
    detailPreviewImages() {
      return this.detailImageMediaList.map(item => item.mediaUrl).filter(Boolean)
    },
    canCloseCurrentOrder() {
      return Boolean(this.detail.id) && !this.detail.closed && Number(this.detail.settlementStatus) !== 2
    },
    canDeleteCurrentOrder() {
      return Boolean(this.detail.id) && Number(this.detail.settlementStatus) !== 2
    }
  },
  created() {
    this.fetchData()
  },
  methods: {
    maskMobile,
    formatRange(startAt, endAt) {
      if (!startAt && !endAt) return '-'
      return `${startAt || '-'} 至 ${endAt || '-'}`
    },
    authStatusText(status) {
      const map = {
        0: '待审核',
        1: '审核通过',
        2: '审核拒绝',
        3: '已撤回'
      }
      return map[Number(status)] || '-'
    },
    authTypeText(type) {
      const map = {
        1: '企业认证',
        2: '个体工商户认证',
        3: '个人认证'
      }
      return map[Number(type)] || (type != null ? String(type) : '-')
    },
    async fetchData() {
      this.list = await getPlatformOrders(this.query)
    },
    async openDetail(row) {
      this.detail = await getPlatformOrderDetail(row.id)
      this.detailVisible = true
    },
    goPrivateMessages(merchantUserId, artistUserId) {
      const query = {}
      if (merchantUserId) query.merchantUserId = String(merchantUserId)
      if (artistUserId) query.artistUserId = String(artistUserId)
      this.$router.push({ path: '/platform/private-messages', query })
    },
    openCloseDialog() {
      this.closeForm = {
        orderId: this.detail.id || '',
        closeReason: ''
      }
      this.closeDialogVisible = true
    },
    async submitCloseOrder() {
      if (!this.closeForm.orderId) return
      const reason = String(this.closeForm.closeReason || '').trim()
      if (!reason) {
        this.$message.warning('请输入关闭原因')
        return
      }
      await closePlatformOrder({
        orderId: this.closeForm.orderId,
        closeReason: reason
      })
      this.$message.success('关闭订单成功')
      this.closeDialogVisible = false
      this.detailVisible = false
      this.detail = {}
      await this.fetchData()
    },
    async handleDeleteOrder() {
      if (!this.detail.id) return
      try {
        await MessageBox.confirm('删除订单当前仅允许对已关闭订单尝试执行，若后端未启用会直接给出提示。确认继续吗？', '删除确认', {
          type: 'warning',
          confirmButtonText: '确认',
          cancelButtonText: '取消'
        })
      } catch (e) {
        return
      }
      await deletePlatformOrder(this.detail.id)
      this.$message.success('删除订单成功')
      this.detailVisible = false
      await this.fetchData()
    },
    mediaTagType(mediaType) {
      if (String(mediaType).includes('视频')) return 'warning'
      if (String(mediaType).includes('待验收')) return 'success'
      return 'primary'
    },
    async openArtistDetailById(userId) {
      if (!userId) return
      this.artistDetail = await getArtistUserDetail(String(userId))
      this.artistDetailVisible = true
    },
    async openMerchantDetailById(userId) {
      if (!userId) return
      this.merchantDetail = await getMerchantUserDetail(String(userId))
      this.merchantDetailVisible = true
    },
    openVideoPreview(item) {
      const normalized = normalizeMediaItem(item)
      this.previewVideo = {
        mediaUrl: normalized ? normalized.mediaUrl : '',
        coverUrl: normalized ? normalized.coverUrl : ''
      }
      this.videoPreviewVisible = true
    },
    handleVideoPreviewClosed() {
      const player = this.$refs.previewVideoPlayer
      if (player && typeof player.pause === 'function') {
        player.pause()
        player.currentTime = 0
      }
      this.previewVideo = {}
    },
    async requestVideoFullscreen() {
      const player = this.$refs.previewVideoPlayer
      if (!player) return
      const requestFullscreen =
        player.requestFullscreen ||
        player.webkitRequestFullscreen ||
        player.mozRequestFullScreen ||
        player.msRequestFullscreen
      if (!requestFullscreen) {
        this.$message.warning('当前浏览器不支持全屏播放')
        return
      }
      await requestFullscreen.call(player)
    }
  }
}
</script>

<style scoped>
.cell-block {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.cell-title {
  color: #303133;
  line-height: 1.4;
}

.cell-sub {
  color: #909399;
  font-size: 12px;
  line-height: 1.4;
}

.inline-link {
  padding: 0;
}

.order-detail-wrap {
  max-height: 70vh;
  overflow-y: auto;
  padding-right: 6px;
}

.detail-section {
  margin-bottom: 18px;
}

.detail-action-bar {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-bottom: 16px;
}

.section-title {
  margin-bottom: 12px;
  font-size: 16px;
  font-weight: 600;
  color: #303133;
}

.source-tag {
  margin-left: 8px;
}

.log-item-title {
  font-weight: 600;
  color: #303133;
}

.log-item-desc {
  margin-top: 4px;
  color: #606266;
}

.media-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 12px;
}

.media-tag-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;
}

.video-grid {
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
}

.detail-media-card {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.media-image {
  width: 100%;
  height: 150px;
  border-radius: 8px;
  background: #f5f7fa;
}

.detail-media-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  color: #606266;
  font-size: 12px;
}

.video-card {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.media-video {
  width: 100%;
  height: 180px;
  display: block;
  border-radius: 8px;
  background: #000;
}

.video-actions {
  display: flex;
  justify-content: flex-end;
}

.video-preview-wrap {
  background: #000;
  border-radius: 12px;
  overflow: hidden;
}

.preview-video-player {
  width: 100%;
  max-height: 72vh;
  display: block;
  background: #000;
}

.auth-material-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 12px;
  margin-top: 16px;
}

.auth-material-card {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.auth-material-label {
  color: #606266;
  font-size: 13px;
}

.auth-material-image {
  width: 100%;
  height: 160px;
  border-radius: 8px;
  background: #f5f7fa;
}

.review-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}

.review-card {
  min-height: 160px;
  padding: 16px;
  border: 1px solid #ebeef5;
  border-radius: 10px;
  background: #fff;
}

.review-title {
  margin-bottom: 10px;
  font-size: 15px;
  font-weight: 600;
  color: #303133;
}

.review-meta {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 10px;
  color: #909399;
  font-size: 12px;
}

.review-content {
  color: #606266;
  line-height: 1.7;
  white-space: pre-wrap;
  word-break: break-all;
}
</style>
